/** Material families, used for sounds, debris behavior and gameplay rules. */
export const MatKind = {
  Generic: 0,
  Stone: 1,
  Dirt: 2,
  Wood: 3,
  Metal: 4,
  Glass: 5,
  Leaves: 6,
  Sand: 7,
  Snow: 8,
  Brick: 9,
  Concrete: 10,
  Plastic: 11,
  Crystal: 12,
  Cloth: 13,
  Ice: 14,
  Bedrock: 15,
} as const;
export type MatKind = (typeof MatKind)[keyof typeof MatKind];

export const MatFlag = {
  Transparent: 1,
  Emissive: 2,
  Flammable: 4,
  /** Objective crystal voxels (Demolition cores). */
  Core: 8,
  /** Placed by players with the builder tool. */
  Built: 16,
  /** Detonates when destroyed (TNT crates / explosive barrels); chains into neighbors. */
  Explosive: 32,
  /** Water: players wade through the top layers of an open column (movement only). */
  Liquid: 64,
  /** Natural ground (grass, dirt, sand, snow, bedrock-side rock): explosions only crater its core. */
  Terrain: 128,
} as const;

/** Surface patterns drawn procedurally by the world shader (no extra geometry). */
export const Pattern = {
  None: 0,
  Bricks: 1,
  Planks: 2,
  Blocks: 3,
  Tiles: 4,
  Slabs: 5,
  Bark: 6,
  Panels: 7,
  Grid: 8,
} as const;

export interface MaterialDef {
  name: string;
  color: number;
  /** Hit points per voxel. 0 = indestructible. */
  hp: number;
  kind: MatKind;
  flags?: number;
  pattern?: number;
}

/**
 * Lookup tables for the 256-entry world palette. Index 0 is air, index 1 is bedrock.
 * Colors are 0xRRGGBB.
 */
export class MaterialTable {
  readonly colors = new Uint32Array(256);
  readonly hp = new Uint8Array(256);
  readonly kind = new Uint8Array(256);
  readonly flags = new Uint8Array(256);
  readonly pattern = new Uint8Array(256);
  readonly names: string[] = new Array(256).fill('');
  readonly byName = new Map<string, number>();

  constructor(defs: readonly (MaterialDef | null)[]) {
    for (let i = 0; i < defs.length && i < 256; i++) {
      const d = defs[i];
      if (!d) continue;
      this.colors[i] = d.color;
      this.hp[i] = d.hp;
      this.kind[i] = d.kind;
      this.flags[i] = d.flags ?? 0;
      this.pattern[i] = d.pattern ?? 0;
      this.names[i] = d.name;
      this.byName.set(d.name, i);
    }
  }

  id(name: string): number {
    const v = this.byName.get(name);
    if (v === undefined) throw new Error(`Unknown material: ${name}`);
    return v;
  }

  isTransparent(v: number): boolean {
    return (this.flags[v] & MatFlag.Transparent) !== 0;
  }
}
